import React from 'react';
import { useImmerReducer } from 'use-immer';
import MultiRef from 'react-multi-ref';
import {
	Box,
	HStack,
	Flex,
	Input,
	NumberInput,
	Switch,
	Text,
	Textarea,
	Button,
	Accordion,
	CloseButton,
	Portal,
	Dialog
} from '@chakra-ui/react';
import theme from '../theme';

function formReducer(draft, action) {
	switch (action.type) {
		case 'add_helmet': {
			const { value } = action.payload;
			draft.fields.dive_helmets.helmets.push(value);
			break;
		}
		case 'remove_helmet': {
			const { index } = action.payload;
			draft.fields.dive_helmets.helmets.splice(index, 1);
			break;
		}
		case 'update_field_value': {
			const { section, field, value } = action.payload;

			if (section) {
				draft.fields[section][field] = value;
			} else {
				draft.fields[field] = value;
			}

			break;
		}
		case 'update_check_text': {
			const { section, field, selected, text } = action.payload;
			if (typeof selected === 'boolean') {
				draft.fields[section][field].selected = selected;
			} else {
				draft.fields[section][field].text = text;
			}

			break;
		}
		default:
			throw new Error();
	}
}

export default ({ currentForm, formData, handoffData, edit, saveForm, isSubmitted }) => {
	const [state, dispatch] = useImmerReducer(formReducer, { fields: formData });

	const [removeHelmetDialogOpen, setRemoveHelmetDialogOpen] = React.useState(null);
	const [diveHelmetRefs] = React.useState(() => new MultiRef());

	React.useEffect(() => {
		saveForm({
			...currentForm,
			form_data: state.fields
		});
	}, [state.fields]);

	return (
		<>
			<Accordion.Root multiple collapsible p="8px" defaultValue={[0, 1, 2, 3, 4, 5, 6, 7]}>
				<Accordion.Item value={0} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Surface Supplied Air (SSA)
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										checked={state.fields.surface_supplied_air.bottle_bank}
										onCheckedChange={() => {
											if (state.fields.surface_supplied_air.dive_compressor) {
												dispatch({
													type: 'update_field_value',
													payload: {
														section: 'surface_supplied_air',
														field: 'dive_compressor',
														value: false
													}
												});
											}

											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'surface_supplied_air',
													field: 'bottle_bank',
													value: !state.fields.surface_supplied_air.bottle_bank
												}
											});
										}}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										HP
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										checked={state.fields.surface_supplied_air.dive_compressor}
										onCheckedChange={() => {
											if (state.fields.surface_supplied_air.bottle_bank) {
												dispatch({
													type: 'update_field_value',
													payload: {
														section: 'surface_supplied_air',
														field: 'bottle_bank',
														value: false
													}
												});
											}
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'surface_supplied_air',
													field: 'dive_compressor',
													value: !state.fields.surface_supplied_air.dive_compressor
												}
											});
										}}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										LP
									</Text>
								</HStack>
							</Flex>
							{state.fields.surface_supplied_air.dive_compressor ? (
								<>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Dive Compressor ID:
											</Text>
											<Input
												bgColor={theme.colors.white}
												value={state.fields.surface_supplied_air.dive_compressor_id}
												fontSize={16}
												onChange={({ target }) =>
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'dive_compressor_id',
															value: target.value
														}
													})
												}
												disabled={isSubmitted && !edit}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</Box>
									</Flex>
									<Flex>
										<HStack
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Switch.Root
												colorPalette="blue"
												size="lg"
												onCheckedChange={() => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'fuel',
															value: !state.fields.surface_supplied_air.fuel
														}
													});
												}}
												checked={state.fields.surface_supplied_air.fuel}
												disabled={isSubmitted && !edit}
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											<Text fontSize={16} as="label" pl="4px">
												Fuel
											</Text>
										</HStack>
										<HStack
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Switch.Root
												colorPalette="blue"
												size="lg"
												onCheckedChange={() => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'volume_tank',
															value: !state.fields.surface_supplied_air.volume_tank
														}
													});
												}}
												checked={state.fields.surface_supplied_air.volume_tank}
												disabled={isSubmitted && !edit}
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											<Text fontSize={16} as="label" pl="4px">
												Volume Tank Drained
											</Text>
										</HStack>
										<HStack
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Switch.Root
												colorPalette="blue"
												size="lg"
												onCheckedChange={() => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'monolec_oil',
															value: !state.fields.surface_supplied_air.monolec_oil
														}
													});
												}}
												checked={state.fields.surface_supplied_air.monolec_oil}
												disabled={isSubmitted && !edit}
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											<Text fontSize={16} as="label" pl="4px">
												Monolec Oil
											</Text>
										</HStack>
									</Flex>
									<Flex>
										<HStack
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Switch.Root
												colorPalette="blue"
												size="lg"
												onCheckedChange={() => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'filters',
															value: !state.fields.surface_supplied_air.filters
														}
													});
												}}
												checked={state.fields.surface_supplied_air.filters}
												disabled={isSubmitted && !edit}
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											<Text fontSize={16} as="label" pl="4px">
												Filters
											</Text>
										</HStack>
										<HStack
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Switch.Root
												colorPalette="blue"
												size="lg"
												onCheckedChange={() => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'engine_oil',
															value: !state.fields.surface_supplied_air.engine_oil
														}
													});
												}}
												checked={state.fields.surface_supplied_air.engine_oil}
												disabled={isSubmitted && !edit}
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											<Text fontSize={16} as="label" pl="4px">
												Engine Oil
											</Text>
										</HStack>
										<HStack
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Switch.Root
												colorPalette="blue"
												size="lg"
												onCheckedChange={() => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'check_for_leaks',
															value: !state.fields.surface_supplied_air.check_for_leaks
														}
													});
												}}
												checked={state.fields.surface_supplied_air.check_for_leaks}
												disabled={isSubmitted && !edit}
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											<Text fontSize={16} as="label" pl="4px">
												Check for Leaks
											</Text>
										</HStack>
									</Flex>
								</>
							) : null}
							{state.fields.surface_supplied_air.bottle_bank ? (
								<>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Bottle 1 (PSI)
											</Text>
											<NumberInput.Root
												value={state.fields.surface_supplied_air.bottle_1_psi}
												disabled={isSubmitted && !edit}
												onValueChange={e => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'bottle_1_psi',
															value: e.value
														}
													});
												}}
											>
												<NumberInput.Input
													fontSize={16}
													bgColor={theme.colors.white}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</NumberInput.Root>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Bottle 1 Regulated To (PSI)
											</Text>
											<NumberInput.Root
												value={state.fields.surface_supplied_air.bottle_1_regulated_to_psi}
												disabled={isSubmitted && !edit}
												onValueChange={e => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'bottle_1_regulated_to_psi',
															value: e.value
														}
													});
												}}
											>
												<NumberInput.Input
													fontSize={16}
													bgColor={theme.colors.white}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</NumberInput.Root>
										</Box>
									</Flex>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Bottle 2 (PSI)
											</Text>
											<NumberInput.Root
												value={state.fields.surface_supplied_air.bottle_2_psi}
												disabled={isSubmitted && !edit}
												onValueChange={e => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'bottle_2_psi',
															value: e.value
														}
													});
												}}
											>
												<NumberInput.Input
													fontSize={16}
													bgColor={theme.colors.white}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</NumberInput.Root>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Bottle 2 Regulated To (PSI)
											</Text>
											<NumberInput.Root
												value={state.fields.surface_supplied_air.bottle_2_regulated_to_psi}
												disabled={isSubmitted && !edit}
												onValueChange={e => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'bottle_2_regulated_to_psi',
															value: e.value
														}
													});
												}}
											>
												<NumberInput.Input
													fontSize={16}
													bgColor={theme.colors.white}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</NumberInput.Root>
										</Box>
									</Flex>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Bottle 3 (PSI)
											</Text>
											<NumberInput.Root
												value={state.fields.surface_supplied_air.bottle_3_psi}
												disabled={isSubmitted && !edit}
												onValueChange={e => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'bottle_3_psi',
															value: e.value
														}
													});
												}}
											>
												<NumberInput.Input
													fontSize={16}
													bgColor={theme.colors.white}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</NumberInput.Root>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Bottle 3 Regulated To (PSI)
											</Text>
											<NumberInput.Root
												value={state.fields.surface_supplied_air.bottle_3_regulated_to_psi}
												disabled={isSubmitted && !edit}
												onValueChange={e => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'surface_supplied_air',
															field: 'bottle_3_regulated_to_psi',
															value: e.value
														}
													});
												}}
											>
												<NumberInput.Input
													fontSize={16}
													bgColor={theme.colors.white}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</NumberInput.Root>
										</Box>
									</Flex>
								</>
							) : null}
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Repairs or Corrections Made
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										spellCheck
										autoCapitalize="sentences"
										multiline
										value={state.fields.surface_supplied_air.repairs_or_corrections_made}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'surface_supplied_air',
													field: 'repairs_or_corrections_made',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={1} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Reserve Air Supply
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										HP Tank (PSI)
									</Text>
									<Flex align="center" w="100%" mx="-8px">
										<Switch.Root
											colorPalette="blue"
											size="lg"
											px="8px"
											onCheckedChange={() =>
												dispatch({
													type: 'update_check_text',
													payload: {
														section: 'reserve_air_supply',
														field: 'hp_tank',
														selected: !state.fields.reserve_air_supply.hp_tank.selected
													}
												})
											}
											checked={state.fields.reserve_air_supply.hp_tank.selected}
											disabled={isSubmitted && !edit}
										>
											<Switch.HiddenInput />
											<Switch.Control />
										</Switch.Root>
										<NumberInput.Root
											w="100%"
											mx="8px"
											disabled={
												!state.fields.reserve_air_supply.hp_tank.selected ||
												(isSubmitted && !edit)
											}
											value={state.fields.reserve_air_supply.hp_tank.text}
											onValueChange={e => {
												dispatch({
													type: 'update_check_text',
													payload: {
														section: 'reserve_air_supply',
														field: 'hp_tank',
														text: e.value
													}
												});
											}}
										>
											<NumberInput.Input
												fontSize={16}
												bgColor={theme.colors.white}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</NumberInput.Root>
									</Flex>
								</Box>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										HP Tank Regulated To (PSI)
									</Text>
									<NumberInput.Root
										value={state.fields.reserve_air_supply.hp_tank_regulated_to}
										disabled={isSubmitted && !edit}
										onValueChange={e => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'reserve_air_supply',
													field: 'hp_tank_regulated_to',
													value: e.value
												}
											});
										}}
									>
										<NumberInput.Input
											fontSize={16}
											bgColor={theme.colors.white}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</NumberInput.Root>
								</Box>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Diver 1 Bailout Bottle (PSI)
									</Text>
									<Flex align="center" w="100%" mx="-8px">
										<Switch.Root
											colorPalette="blue"
											size="lg"
											px="8px"
											onCheckedChange={() => {
												dispatch({
													type: 'update_check_text',
													payload: {
														section: 'reserve_air_supply',
														field: 'diver_1_bailout_bottle',
														selected:
															!state.fields.reserve_air_supply.diver_1_bailout_bottle
																.selected
													}
												});
											}}
											checked={state.fields.reserve_air_supply.diver_1_bailout_bottle.selected}
											disabled={isSubmitted && !edit}
										>
											<Switch.HiddenInput />
											<Switch.Control />
										</Switch.Root>
										<NumberInput.Root
											w="100%"
											mx="8px"
											value={state.fields.reserve_air_supply.diver_1_bailout_bottle.text}
											disabled={
												!state.fields.reserve_air_supply.diver_1_bailout_bottle.selected ||
												(isSubmitted && !edit)
											}
											onValueChange={e => {
												dispatch({
													type: 'update_check_text',
													payload: {
														section: 'reserve_air_supply',
														field: 'diver_1_bailout_bottle',
														text: e.value
													}
												});
											}}
										>
											<NumberInput.Input
												fontSize={16}
												bgColor={theme.colors.white}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</NumberInput.Root>
									</Flex>
								</Box>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Diver 2 Bailout Bottle (PSI)
									</Text>
									<Flex align="center" w="100%" mx="-8px">
										<Switch.Root
											colorPalette="blue"
											size="lg"
											px="8px"
											onCheckedChange={() => {
												dispatch({
													type: 'update_check_text',
													payload: {
														section: 'reserve_air_supply',
														field: 'diver_2_bailout_bottle',
														selected:
															!state.fields.reserve_air_supply.diver_2_bailout_bottle
																.selected
													}
												});
											}}
											checked={state.fields.reserve_air_supply.diver_2_bailout_bottle.selected}
											disabled={isSubmitted && !edit}
										>
											<Switch.HiddenInput />
											<Switch.Control />
										</Switch.Root>
										<NumberInput.Root
											w="100%"
											mx="8px"
											value={state.fields.reserve_air_supply.diver_2_bailout_bottle.text}
											disabled={
												!state.fields.reserve_air_supply.diver_2_bailout_bottle.selected ||
												(isSubmitted && !edit)
											}
											onValueChange={e => {
												dispatch({
													type: 'update_check_text',
													payload: {
														section: 'reserve_air_supply',
														field: 'diver_2_bailout_bottle',
														text: e.value
													}
												});
											}}
										>
											<NumberInput.Input
												fontSize={16}
												bgColor={theme.colors.white}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</NumberInput.Root>
									</Flex>
								</Box>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Diver 3 Bailout Bottle (PSI)
									</Text>
									<Flex align="center" w="100%" mx="-8px">
										<Switch.Root
											colorPalette="blue"
											size="lg"
											px="8px"
											onCheckedChange={() => {
												dispatch({
													type: 'update_check_text',
													payload: {
														section: 'reserve_air_supply',
														field: 'diver_3_bailout_bottle',
														selected:
															!state.fields.reserve_air_supply.diver_3_bailout_bottle
																.selected
													}
												});
											}}
											checked={state.fields.reserve_air_supply.diver_3_bailout_bottle.selected}
											disabled={isSubmitted && !edit}
										>
											<Switch.HiddenInput />
											<Switch.Control />
										</Switch.Root>
										<NumberInput.Root
											w="100%"
											mx="8px"
											value={state.fields.reserve_air_supply.diver_3_bailout_bottle.text}
											disabled={
												!state.fields.reserve_air_supply.diver_3_bailout_bottle.selected ||
												(isSubmitted && !edit)
											}
											onValueChange={e => {
												dispatch({
													type: 'update_check_text',
													payload: {
														section: 'reserve_air_supply',
														field: 'diver_3_bailout_bottle',
														text: e.value
													}
												});
											}}
										>
											<NumberInput.Input
												fontSize={16}
												bgColor={theme.colors.white}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</NumberInput.Root>
									</Flex>
								</Box>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'reserve_air_supply',
													field: 'tanks_secure',
													value: !state.fields.reserve_air_supply.tanks_secure
												}
											});
										}}
										checked={state.fields.reserve_air_supply.tanks_secure}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Tanks Secure
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Repairs or Corrections Made
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										spellCheck
										autoCapitalize="sentences"
										multiline
										value={state.fields.reserve_air_supply.repairs_or_corrections_made}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'reserve_air_supply',
													field: 'repairs_or_corrections_made',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={2} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Umbilicals, Whips, And Pressure Gauges
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'umbilicals_whips_pressure_gauges',
													field: 'connection_tight',
													value: !state.fields.umbilicals_whips_pressure_gauges
														.connection_tight
												}
											});
										}}
										checked={state.fields.umbilicals_whips_pressure_gauges.connection_tight}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Connection Tight
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'umbilicals_whips_pressure_gauges',
													field: 'main_valves_open',
													value: !state.fields.umbilicals_whips_pressure_gauges
														.main_valves_open
												}
											});
										}}
										checked={state.fields.umbilicals_whips_pressure_gauges.main_valves_open}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Main Valves Open
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'umbilicals_whips_pressure_gauges',
													field: 'check_for_leaks',
													value: !state.fields.umbilicals_whips_pressure_gauges
														.check_for_leaks
												}
											});
										}}
										checked={state.fields.umbilicals_whips_pressure_gauges.check_for_leaks}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Check for Leaks
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Repairs or Corrections Made
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										spellCheck
										autoCapitalize="sentences"
										multiline
										value={
											state.fields.umbilicals_whips_pressure_gauges.repairs_or_corrections_made
										}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'umbilicals_whips_pressure_gauges',
													field: 'repairs_or_corrections_made',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={3} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Dive Helmets
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Box w="100%">
								<Text fontSize={16} as="label" pl="8px">
									Helmets
								</Text>
								{state.fields.dive_helmets.helmets.length ? (
									<>
										{state.fields.dive_helmets.helmets.map((helmet, index) => (
											<Flex key={index} align="center">
												<Box
													w="100%"
													p="8px 16px"
													m="4px 8px"
													bgColor={theme.colors.gray[500]}
													borderRadius="base"
												>
													<Text fontSize={16} fontWeight={800}>
														{helmet}
													</Text>
												</Box>
												{!isSubmitted || edit ? (
													<>
														<CloseButton
															size="md"
															m="0 8px"
															height="40px"
															width="40px"
															color={theme.colors.white}
															bgColor={theme.colors.red[500]}
															_hover={{ background: theme.colors.red[600] }}
															disabled={isSubmitted && !edit}
															onClick={() => setRemoveHelmetDialogOpen(index)}
														/>
														<Dialog.Root
															role="alertdialog"
															initialFocusEl={diveHelmetRefs.map.get(index)}
															onOpenChange={({ open }) => {
																if (!open) {
																	setRemoveHelmetDialogOpen(null);
																}
															}}
															open={removeHelmetDialogOpen === index}
															placement="center"
														>
															<Portal>
																<Dialog.Backdrop />
																<Dialog.Positioner>
																	<Dialog.Content opacity="1 !important">
																		<Dialog.Header>
																			<Dialog.Title
																				fontSize="lg"
																				fontWeight="bold"
																			>
																				Confirm Delete
																			</Dialog.Title>
																		</Dialog.Header>
																		<Dialog.Body>
																			<p>
																				Are you sure you want to remove this
																				item?
																			</p>
																		</Dialog.Body>
																		<Dialog.Footer>
																			<Dialog.ActionTrigger asChild>
																				<Button
																					size="lg"
																					ref={diveHelmetRefs.ref(index)}
																					onClick={() =>
																						setRemoveHelmetDialogOpen(null)
																					}
																				>
																					Cancel
																				</Button>
																			</Dialog.ActionTrigger>
																			<Button
																				colorPalette="red"
																				size="lg"
																				ml={3}
																				bg={theme.colors.red[500]}
																				_hover={{
																					background: theme.colors.red[600]
																				}}
																				color={theme.colors.white}
																				onClick={() => {
																					setRemoveHelmetDialogOpen(null);
																					dispatch({
																						type: 'remove_helmet',
																						payload: { index }
																					});
																				}}
																			>
																				Delete Item
																			</Button>
																		</Dialog.Footer>
																		<Dialog.CloseTrigger asChild>
																			<CloseButton size="sm" />
																		</Dialog.CloseTrigger>
																	</Dialog.Content>
																</Dialog.Positioner>
															</Portal>
														</Dialog.Root>
													</>
												) : null}
											</Flex>
										))}
									</>
								) : null}
							</Box>
							<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
								<Text fontSize={16} as="label" pb="4px">
									Add Helmet
								</Text>
								<Flex align="flex-end">
									<Input
										bgColor={theme.colors.white}
										value={state.fields.dive_helmets.helmet}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_helmets',
													field: 'helmet',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
									<Button
										size="lg"
										m="0 8px"
										color={theme.colors.white}
										bg={theme.colors.blue[700]}
										_hover={{ backgroundColor: theme.colors.blue[800] }}
										onClick={() => {
											dispatch({
												type: 'add_helmet',
												payload: {
													value: state.fields.dive_helmets.helmet
												}
											});
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_helmets',
													field: 'helmet',
													value: ''
												}
											});
										}}
										disabled={isSubmitted && !edit}
									>
										Add
									</Button>
								</Flex>
							</Box>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_helmets',
													field: 'helmet_o_rings',
													value: !state.fields.dive_helmets.helmet_o_rings
												}
											});
										}}
										checked={state.fields.dive_helmets.helmet_o_rings}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Helmet O-Rings
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_helmets',
													field: 'neck_dam',
													value: !state.fields.dive_helmets.neck_dam
												}
											});
										}}
										checked={state.fields.dive_helmets.neck_dam}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Neck Dam
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_helmets',
													field: 'purge_valve',
													value: !state.fields.dive_helmets.purge_valve
												}
											});
										}}
										checked={state.fields.dive_helmets.purge_valve}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Purge Valve
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_helmets',
													field: 'non_return_value',
													value: !state.fields.dive_helmets.non_return_value
												}
											});
										}}
										checked={state.fields.dive_helmets.non_return_value}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Non Return Valve
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_helmets',
													field: 'oral_nasal',
													value: !state.fields.dive_helmets.oral_nasal
												}
											});
										}}
										checked={state.fields.dive_helmets.oral_nasal}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Oral Nasal
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_helmets',
													field: 'communications',
													value: !state.fields.dive_helmets.communications
												}
											});
										}}
										checked={state.fields.dive_helmets.communications}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Communications
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_helmets',
													field: 'kmb_hood',
													value: !state.fields.dive_helmets.kmb_hood
												}
											});
										}}
										checked={state.fields.dive_helmets.kmb_hood}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										KMB Hood
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_helmets',
													field: 'snoopy',
													value: !state.fields.dive_helmets.snoopy
												}
											});
										}}
										checked={state.fields.dive_helmets.snoopy}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Snoopy
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Repairs or Corrections Made
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										spellCheck
										autoCapitalize="sentences"
										value={state.fields.dive_helmets.repairs_or_corrections_made}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_helmets',
													field: 'repairs_or_corrections_made',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={4} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Thermal Protection (Suits)
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'thermal_protection_suits',
													field: 'wet_suit',
													value: !state.fields.thermal_protection_suits.wet_suit
												}
											});
										}}
										checked={state.fields.thermal_protection_suits.wet_suit}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Wet Suit
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'thermal_protection_suits',
													field: 'dry_suit',
													value: !state.fields.thermal_protection_suits.dry_suit
												}
											});
										}}
										checked={state.fields.thermal_protection_suits.dry_suit}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Dry Suit
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'thermal_protection_suits',
													field: 'inflate_valves',
													value: !state.fields.thermal_protection_suits.inflate_valves
												}
											});
										}}
										checked={state.fields.thermal_protection_suits.inflate_valves}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Inflate Valves
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'thermal_protection_suits',
													field: 'hot_water',
													value: !state.fields.thermal_protection_suits.hot_water
												}
											});
										}}
										checked={state.fields.thermal_protection_suits.hot_water}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Hot Water
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'thermal_protection_suits',
													field: 'dry_suit_valves',
													value: !state.fields.thermal_protection_suits.dry_suit_valves
												}
											});
										}}
										checked={state.fields.thermal_protection_suits.dry_suit_valves}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Dry Suit Valves
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'thermal_protection_suits',
													field: 'condition_of_suit',
													value: !state.fields.thermal_protection_suits.condition_of_suit
												}
											});
										}}
										checked={state.fields.thermal_protection_suits.condition_of_suit}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Condition of Suit
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Repairs or Corrections Made
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										spellCheck
										autoCapitalize="sentences"
										multiline
										value={state.fields.thermal_protection_suits.repairs_or_corrections_made}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'thermal_protection_suits',
													field: 'repairs_or_corrections_made',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={5} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Diver Communications
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'communications',
													field: 'batteries_charged',
													value: !state.fields.communications.batteries_charged
												}
											});
										}}
										checked={state.fields.communications.batteries_charged}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Batteries Charged
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'communications',
													field: 'connections_good',
													value: !state.fields.communications.connections_good
												}
											});
										}}
										checked={state.fields.communications.connections_good}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Connections Good
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'communications',
													field: 'test_diver_1',
													value: !state.fields.communications.test_diver_1
												}
											});
										}}
										checked={state.fields.communications.test_diver_1}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Test Comm Box Diver 1
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'communications',
													field: 'test_diver_2',
													value: !state.fields.communications.test_diver_2
												}
											});
										}}
										checked={state.fields.communications.test_diver_2}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Test Comm Box Diver 2
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Repairs or Corrections Made
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										spellCheck
										autoCapitalize="sentences"
										multiline
										value={state.fields.communications.repairs_or_corrections_made}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'communications',
													field: 'repairs_or_corrections_made',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={6} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Emergency Communication
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_communication',
													field: 'emergency_phone_list',
													value: !state.fields.emergency_communication.emergency_phone_list
												}
											});
										}}
										checked={state.fields.emergency_communication.emergency_phone_list}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Emergency Phone List
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_communication',
													field: 'marine_radio_test',
													value: !state.fields.emergency_communication.marine_radio_test
												}
											});
										}}
										checked={state.fields.emergency_communication.marine_radio_test}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Marine Radio
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_communication',
													field: 'cellular_phone_activated',
													value: !state.fields.emergency_communication
														.cellular_phone_activated
												}
											});
										}}
										checked={state.fields.emergency_communication.cellular_phone_activated}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Cellular Phone Coverage
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Repairs or Corrections Made
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										spellCheck
										autoCapitalize="sentences"
										value={state.fields.emergency_communication.repairs_or_corrections_made}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_communication',
													field: 'repairs_or_corrections_made',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={7} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							General
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'general',
													field: 'dive_tables',
													value: !state.fields.general.dive_tables
												}
											});
										}}
										checked={state.fields.general.dive_tables}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Dive Tables
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'general',
													field: 'dive_flags_out',
													value: !state.fields.general.dive_flags_out
												}
											});
										}}
										checked={state.fields.general.dive_flags_out}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Dive Flags Out
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'general',
													field: 'dive_platform_securely_anchored',
													value: !state.fields.general.dive_platform_securely_anchored
												}
											});
										}}
										checked={state.fields.general.dive_platform_securely_anchored}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Dive Platform Securely Anchored
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'general',
													field: 'dive_knife',
													value: !state.fields.general.dive_knife
												}
											});
										}}
										checked={state.fields.general.dive_knife}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Dive Knife
									</Text>
								</HStack>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
			</Accordion.Root>
			<Flex>
				<HStack
					w="100%"
					p="8px"
					m="16px"
					bgColor={theme.colors.gray[500]}
					borderRadius="base"
					borderBottom={`2px solid ${handoffData?.diving && !state.fields.complete ? 'red' : 'transparent'}`}
				>
					<Switch.Root
						colorPalette="blue"
						size="lg"
						disabled={isSubmitted && !edit}
						onCheckedChange={() => {
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'complete',
									value: !state.fields.complete
								}
							});
						}}
						checked={state.fields.complete}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						Form Is Complete
					</Text>
				</HStack>
			</Flex>
		</>
	);
};
